
import { defineComponent, ref, onMounted } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import { PostcardRewardLog, PostcardRewardStats } from "@/store/modules/PostcardRewardModule"
import { POINT_LABEL } from "@/core/config/labels"

export default defineComponent({
  setup() {
    onMounted(() => {
      setCurrentPageBreadcrumbs("明信片奖励发放记录", ["明信片奖励管理;postcard_reward_logs"])
    })

    const store = useStore()
    const tableData = ref<(PostcardRewardLog & { retrying?: boolean })[]>([])
    const stats = ref<PostcardRewardStats>({} as PostcardRewardStats)

    const formData = ref({
      reward_date: '',
      status: '',
      item_type: '',
      user_mobile: ''
    })

    const page = ref({
      currentPage: 1,
      pageSize: 20,
      totalResult: 0,
    })

    const distributeDialogVisible = ref(false)
    const distributeLoading = ref(false)
    const distributeForm = ref({
      date: new Date(Date.now() - 86400000).toISOString().slice(0, 10), // 默认昨天
      force: false
    })

    const retryLoading = ref(false)

    const query = () => {
      store
        .dispatch(Actions.GET_POSTCARD_REWARD_LOGS, { ...formData.value, ...page.value })
        .then(() => {
          tableData.value = store.getters.currentPostcardRewardLogs.map((log: PostcardRewardLog) => ({
            ...log,
            retrying: false
          }))
          page.value.totalResult = store.getters.currentPostcardRewardLogCount
        })
        .catch(() => {
          ElMessage.error('获取发放记录失败')
        })

      // 同时获取统计数据
      loadStats()
    }

    const loadStats = () => {
      const statsDate = formData.value.reward_date || new Date(Date.now() - 86400000).toISOString().slice(0, 10)
      store
        .dispatch(Actions.GET_POSTCARD_REWARD_STATS, { date: statsDate })
        .then(() => {
          stats.value = store.getters.currentPostcardRewardStats
        })
        .catch(() => {
          // 忽略统计错误
        })
    }

    query()

    const submitEvent = () => {
      page.value.currentPage = 1
      query()
    }

    const onPageChange = (e: { currentPage: number; pageSize: number }) => {
      page.value.currentPage = e.currentPage
      page.value.pageSize = e.pageSize
      query()
    }

    const onReset = () => {
      formData.value = {
        reward_date: '',
        status: '',
        item_type: '',
        user_mobile: ''
      }
      page.value.currentPage = 1
      query()
    }

    const formatDate = (dateStr: string) => {
      if (!dateStr) return ''
      return dateStr.slice(0, 16).replace('T', ' ')
    }

    const showDistributeDialog = () => {
      distributeDialogVisible.value = true
    }

    const doDistribute = () => {
      if (!distributeForm.value.date) {
        ElMessage.error('请选择发放日期')
        return
      }

      distributeLoading.value = true
      store
        .dispatch(Actions.DISTRIBUTE_POSTCARD_REWARDS, distributeForm.value)
        .then((data: any) => {
          ElMessage.success(data.message || '发放完成')
          distributeDialogVisible.value = false
          query()
        })
        .catch((err: any) => {
          ElMessage.error(err?.error || '发放失败')
        })
        .finally(() => {
          distributeLoading.value = false
        })
    }

    const retryAllFailed = () => {
      retryLoading.value = true
      const params = formData.value.reward_date ? { date: formData.value.reward_date } : {}

      store
        .dispatch(Actions.RETRY_FAILED_POSTCARD_REWARDS, params)
        .then((data: any) => {
          ElMessage.success(`重试完成：成功 ${data.result?.success || 0}，失败 ${data.result?.failed || 0}`)
          query()
        })
        .catch((err: any) => {
          ElMessage.error(err?.error || '重试失败')
        })
        .finally(() => {
          retryLoading.value = false
        })
    }

    const retrySingle = (row: PostcardRewardLog & { retrying?: boolean }) => {
      row.retrying = true
      store
        .dispatch(Actions.RETRY_POSTCARD_REWARD_LOG, row.id)
        .then((data: any) => {
          ElMessage.success(data.message || '重试成功')
          query()
        })
        .catch((err: any) => {
          ElMessage.error(err?.error || '重试失败')
          query()
        })
        .finally(() => {
          row.retrying = false
        })
    }

    const pointLabel = POINT_LABEL

    return {
      pointLabel,
      tableData,
      stats,
      formData,
      submitEvent,
      page,
      onPageChange,
      onReset,
      formatDate,
      distributeDialogVisible,
      distributeLoading,
      distributeForm,
      showDistributeDialog,
      doDistribute,
      retryLoading,
      retryAllFailed,
      retrySingle
    }
  },
})
